import DOMPurify from 'dompurify';

export interface WikiArticle {
  title: string;
  html: string;
  extract?: string;
}

const WIKI_API_BASE = 'https://en.wikipedia.org/api/rest_v1';
const WIKI_ACTION_API = 'https://en.wikipedia.org/w/api.php';

export async function fetchArticle(title: string): Promise<WikiArticle> {
  const encodedTitle = encodeURIComponent(title.replace(/ /g, '_'));
  
  try {
    // First, resolve any redirects
    const resolvedTitle = await resolveRedirect(title);
    const finalTitle = resolvedTitle || title;
    const finalEncodedTitle = encodeURIComponent(finalTitle.replace(/ /g, '_'));
    
    // Fetch the HTML content
    const response = await fetch(
      `${WIKI_API_BASE}/page/html/${finalEncodedTitle}`,
      {
        headers: {
          'Accept': 'text/html; charset=utf-8',
        },
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to fetch article: ${response.status}`);
    }
    
    const html = await response.text();
    
    // Sanitize and process the HTML
    const processedHtml = processWikiHtml(html);
    
    return {
      title: finalTitle.replace(/_/g, ' '),
      html: processedHtml,
    };
  } catch (error) {
    console.error('Error fetching Wikipedia article:', error);
    throw error;
  }
}

async function resolveRedirect(title: string): Promise<string | null> {
  const params = new URLSearchParams({
    action: 'query',
    titles: title,
    redirects: '1',
    format: 'json',
    origin: '*',
  });
  
  try {
    const response = await fetch(`${WIKI_ACTION_API}?${params}`);
    const data = await response.json();
    
    if (data.query?.redirects?.length > 0) {
      return data.query.redirects[data.query.redirects.length - 1].to;
    }
    
    // Get the normalized title if available
    if (data.query?.normalized?.length > 0) {
      return data.query.normalized[0].to;
    }
    
    return null;
  } catch {
    return null;
  }
}

function processWikiHtml(html: string): string {
  // Configure DOMPurify with strict settings - only allow specific safe elements and attributes
  // Note: ALLOW_DATA_ATTR is intentionally set to false to prevent arbitrary data-* attributes
  // Only data-title is explicitly allowed for internal link navigation
  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'dl', 'dt', 'dd',
      'table', 'thead', 'tbody', 'tr', 'th', 'td', 'caption',
      'a', 'span', 'div', 'section', 'article',
      'b', 'i', 'strong', 'em', 'u', 's', 'sup', 'sub',
      'blockquote', 'cite', 'code', 'pre',
      'img', 'figure', 'figcaption',
      'br', 'hr',
    ],
    ALLOWED_ATTR: [
      'href', 'title', 'alt', 'src', 'class', 'id',
      'colspan', 'rowspan', 'scope',
      'data-title', // Only specific data attribute needed for internal link navigation
    ],
    ALLOW_DATA_ATTR: false, // Explicitly disable arbitrary data-* attributes for security
    FORBID_TAGS: ['style', 'script', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'style'],
  });
  
  // Create a temporary DOM to process links
  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitized, 'text/html');
  
  // Process all links
  const links = doc.querySelectorAll('a');
  links.forEach((link) => {
    const href = link.getAttribute('href');
    
    if (href) {
      // Check if it's an internal Wikipedia link
      if (href.startsWith('./') || href.startsWith('/wiki/')) {
        // Extract the article title
        let articleTitle = href
          .replace(/^\.\//, '')
          .replace(/^\/wiki\//, '')
          .split('#')[0]; // Remove anchors
        
        // Decode the title
        try {
          articleTitle = decodeURIComponent(articleTitle);
        } catch {
          // Keep original if decode fails
        }
        
        // Skip special pages, files, categories, etc.
        const skipPrefixes = [
          'File:', 'Image:', 'Category:', 'Template:', 'Wikipedia:',
          'Help:', 'Portal:', 'Special:', 'Talk:', 'User:', 'Module:',
        ];
        
        const shouldSkip = skipPrefixes.some(prefix => 
          articleTitle.toLowerCase().startsWith(prefix.toLowerCase())
        );
        
        if (shouldSkip) {
          // Remove the link but keep the text
          const textNode = document.createTextNode(link.textContent || '');
          link.replaceWith(textNode);
        } else {
          // Convert to our internal link format
          link.setAttribute('href', '#');
          link.setAttribute('data-title', articleTitle);
          link.removeAttribute('target');
        }
      } else if (href.startsWith('http') || href.startsWith('//')) {
        // External link - remove it
        const textNode = document.createTextNode(link.textContent || '');
        link.replaceWith(textNode);
      } else if (href.startsWith('#')) {
        // Anchor link - keep but disable navigation
        link.setAttribute('href', '#');
        link.removeAttribute('data-title');
      }
    }
  });
  
  // Remove unwanted elements
  const selectorsToRemove = [
    '.mw-editsection',
    '.reference',
    '.reflist',
    '.navbox',
    '.sidebar',
    '.infobox',
    '.toc',
    '.mw-empty-elt',
    'style',
    'script',
    '.mw-jump-link',
    '.noprint',
    '[role="navigation"]',
  ];
  
  selectorsToRemove.forEach((selector) => {
    doc.querySelectorAll(selector).forEach((el) => el.remove());
  });
  
  // Process images to use Wikipedia URLs
  const images = doc.querySelectorAll('img');
  images.forEach((img) => {
    const src = img.getAttribute('src');
    if (src && !src.startsWith('http')) {
      img.setAttribute('src', `https:${src}`);
    }
    // Limit image sizes
    img.style.maxWidth = '300px';
    img.style.height = 'auto';
  });
  
  return doc.body.innerHTML;
}

export async function getRandomArticle(): Promise<string> {
  const params = new URLSearchParams({
    action: 'query',
    list: 'random',
    rnnamespace: '0', // Main namespace only
    rnlimit: '1',
    format: 'json',
    origin: '*',
  });
  
  const response = await fetch(`${WIKI_ACTION_API}?${params}`);
  const data = await response.json();
  
  return data.query.random[0].title;
}

export async function searchArticles(query: string, limit: number = 10): Promise<string[]> {
  const params = new URLSearchParams({
    action: 'opensearch',
    search: query,
    limit: limit.toString(),
    namespace: '0',
    format: 'json',
    origin: '*',
  });
  
  const response = await fetch(`${WIKI_ACTION_API}?${params}`);
  const data = await response.json();
  
  return data[1] || [];
}

export function extractArticleTitle(html: string): string | null {
  const match = html.match(/<title>([^<]+)<\/title>/);
  return match ? match[1].replace(' - Wikipedia', '') : null;
}
